import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  LinearProgress,
  Paper,
  Stack,
  Divider,
  TablePagination
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DeleteIcon from '@mui/icons-material/Delete';

const ResearchJobsTable = ({ 
  jobs, 
  isLoading,
  onViewResults,
  onPauseJob,
  onResumeJob,
  onDeleteJob,
  isFilterJob = false,
  qualifiedCounts = {}
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);


  // Format date consistently
  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '—';
      return date.toLocaleString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return '—';
    }
  };

  // Status styling with Wyspur theme colors
  const getStatusConfig = (status) => {
    switch(status.toLowerCase()) {
      case 'completed':
        return { color: '#0095FF', bg: 'rgba(0, 149, 255, 0.1)', label: 'Completed' }; // Wyspur blue
      case 'processing':
        return { color: '#FFD700', bg: 'rgba(255, 215, 0, 0.1)', label: 'Processing' }; // Wyspur yellow
      case 'paused':
        return { color: '#ff9800', bg: 'rgba(255, 152, 0, 0.1)', label: 'Paused' }; // Orange for paused
      case 'failed':
        return { color: '#FF1493', bg: 'rgba(255, 20, 147, 0.1)', label: 'Failed' }; // Wyspur pink
      default:
        return { color: '#9e9e9e', bg: 'rgba(158, 158, 158, 0.1)', label: status };
    }
  };

  // Calculate relevance score display
  const formatRelevanceScore = (score) => {
    if (score === null || score === undefined) return '—';
    const scoreValue = parseFloat(score);
    if (isNaN(scoreValue)) return '—';
    
    let scorePercent;
    if (scoreValue > 0 && scoreValue <= 10) {
      scorePercent = Math.round(scoreValue * 10);
    } else {
      scorePercent = Math.round(scoreValue);
    }
    return `${scorePercent}%`;
  };


  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Get current page data
  const paginatedJobs = jobs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (isLoading && jobs.length === 0) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: 400,
        color: 'text.secondary'
      }}>
        <Typography>Loading lists...</Typography>
      </Box>
    );
  }

  if (jobs.length === 0) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: 400,
        color: 'text.secondary'
      }}>
        <Typography>No research lists found. Create a new research list to get started.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header */}
      <Box sx={{ 
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          md: '2fr 1fr 1fr 1fr 1fr 1fr' // Same spacing for both tabs
        },
        gap: 2,
        p: 2,
        mb: 1,
        borderRadius: 2,
        backgroundColor: 'rgba(255, 255, 255, 0.02)',
        border: '1px solid rgba(255, 255, 255, 0.05)'
      }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, display: { xs: 'none', md: 'block' } }}>
          List Name
        </Typography>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, textAlign: 'center', display: { xs: 'none', md: 'block' } }}>
          Status
        </Typography>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, textAlign: 'center', display: { xs: 'none', md: 'block' } }}>
          Created
        </Typography>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, textAlign: 'center', display: { xs: 'none', md: 'block' } }}>
          Completion
        </Typography>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, textAlign: 'center', display: { xs: 'none', md: 'block' } }}>
          Qualified/Total
        </Typography>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, textAlign: 'center', display: { xs: 'none', md: 'block' } }}>
          Actions
        </Typography>
      </Box>

      {/* Data Rows */}
      <Stack spacing={1}>
        {paginatedJobs.map((job, index) => {
          // Check if this is the first completed job in the entire jobs array (not just paginated)
          const allCompletedJobs = jobs.filter(j => j.status.toLowerCase() === 'completed');
          const isFirstCompletedJob = allCompletedJobs.length > 0 && job.jobId === allCompletedJobs[0].jobId;
          const statusConfig = getStatusConfig(job.status);
          const progress = job.totalWebsites > 0 
            ? Math.round((job.processedWebsites / job.totalWebsites) * 100) 
            : 0;
          
          const isPaused = job.status.toLowerCase() === 'paused';
          const isRunning = job.status.toLowerCase() === 'processing';
          const isFailed = job.status.toLowerCase() === 'failed';
          const isCreated = job.status.toLowerCase() === 'created';
          const canResume = isPaused || isRunning || isFailed || isCreated;
          const canPause = isRunning;

          return (
            <Paper
              key={job.jobId}
              sx={{
                p: 2,
                borderRadius: 3,
                backgroundColor: 'rgba(255, 255, 255, 0.02)',
                border: '1px solid rgba(255, 255, 255, 0.05)',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.04)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
                }
              }}
            >
              <Box sx={{ 
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: '2fr 1fr 1fr 1fr 1fr 1fr' // Same spacing for both tabs
                },
                gap: 2,
                alignItems: 'center'
              }}>
                {/* List Name */}
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 500, mb: { xs: 0.5, md: 0 } }}>
                    {job.name || job.instructions || 'Unnamed List'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'block', md: 'none' } }}>
                    Created: {formatDate(job.createdAt)}
                  </Typography>
                </Box>

                {/* Status */}
                <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'center' } }}>
                  <Chip
                    label={statusConfig.label}
                    size="small"
                    sx={{
                      backgroundColor: statusConfig.bg,
                      color: statusConfig.color,
                      fontWeight: 500,
                      border: `1px solid ${statusConfig.color}30`
                    }}
                  />
                </Box>

                {/* Created */}
                <Box sx={{ textAlign: { xs: 'left', md: 'center' }, display: { xs: 'none', md: 'block' } }}>
                  <Typography variant="body2" color="text.secondary">
                    {formatDate(job.createdAt)}
                  </Typography>
                </Box>

                {/* Completion */}
                <Box sx={{ textAlign: { xs: 'left', md: 'center' } }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                    {job.processedWebsites || 0}/{job.totalWebsites || 0} {isFilterJob ? 'Columns' : ''} ({progress}%)
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: progress === 100 ? '#0095FF' : '#FFD700' // Wyspur blue when complete, yellow when processing
                      }
                    }}
                  />
                </Box>

                {/* Qualified/Total */}
                <Box sx={{ textAlign: { xs: 'left', md: 'center' } }}>
                  <Typography variant="body2">
                    {isFilterJob ? (
                      job.status.toLowerCase() === 'completed' && qualifiedCounts[job.jobId]
                        ? `${qualifiedCounts[job.jobId].qualified}/${qualifiedCounts[job.jobId].total}`
                        : <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            Processing...
                          </Typography>
                    ) : (
                      `${job.keptCount || 0}/${job.processedWebsites || 0}`
                    )}
                  </Typography>
                </Box>

                {/* Actions */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'flex-start', // Always left-aligned so View Results is always the starting point
                  gap: 0.5
                }}>
                  <Tooltip title={job.status.toLowerCase() === 'completed' ? "View Results" : "Results available when job completes"}>
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => onViewResults(job)}
                        disabled={job.status.toLowerCase() !== 'completed'}
                        data-view-results-action={isFirstCompletedJob ? "true" : undefined}
                        sx={{
                          color: job.status.toLowerCase() === 'completed' 
                            ? 'rgba(255, 255, 255, 0.7)' 
                            : 'rgba(255, 255, 255, 0.3)',
                          '&:hover': job.status.toLowerCase() === 'completed' ? {
                            color: '#0095FF', // Wyspur blue
                            backgroundColor: 'rgba(0, 149, 255, 0.1)'
                          } : {},
                          '&:disabled': {
                            color: 'rgba(255, 255, 255, 0.3)'
                          }
                        }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>

                  {canPause && (
                    <Tooltip title="Pause">
                      <IconButton
                        size="small"
                        onClick={() => onPauseJob(job)}
                        sx={{
                          color: 'rgba(255, 255, 255, 0.7)',
                          '&:hover': {
                            color: '#ff9800', // Orange for pause
                            backgroundColor: 'rgba(255, 152, 0, 0.1)'
                          }
                        }}
                      >
                        <PauseIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}

                  {canResume && (
                    <Tooltip title="Resume">
                      <IconButton
                        size="small"
                        onClick={() => onResumeJob(job)}
                        sx={{
                          color: 'rgba(255, 255, 255, 0.7)',
                          '&:hover': {
                            color: '#FFD700', // Wyspur yellow for resume
                            backgroundColor: 'rgba(255, 215, 0, 0.1)'
                          }
                        }}
                      >
                        <PlayArrowIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}

                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      onClick={() => onDeleteJob(job)}
                      sx={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        '&:hover': {
                          color: '#FF1493', // Wyspur pink for delete
                          backgroundColor: 'rgba(255, 20, 147, 0.1)'
                        }
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Paper>
          );
        })}
      </Stack>

      {/* Pagination */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        mt: 3,
        '& .MuiTablePagination-root': {
          color: 'rgba(255, 255, 255, 0.7)'
        },
        '& .MuiTablePagination-select': {
          color: 'rgba(255, 255, 255, 0.9)'
        },
        '& .MuiIconButton-root': {
          color: 'rgba(255, 255, 255, 0.7)',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)'
          },
          '&.Mui-disabled': {
            color: 'rgba(255, 255, 255, 0.3)'
          }
        }
      }}>
        <TablePagination
          component="div"
          count={jobs.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Box>
    </Box>
  );
};

export default ResearchJobsTable;